import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import qs from 'query-string';
import { Observable, Subject, Subscription } from 'rxjs';
import { DataResponse } from 'shared/common';
import { AbstractApiService } from 'src/app/nio-common/services/abstract-api-service.service';
import { Logger, LogService } from 'src/app/nio-common/util';
import { environment } from 'src/environments/environment';
import { PaginatedPaidPhotosResponse, PhotoEventResponse } from '../models/photo.model';
import { InitiateQueueResponse, QueueResponse, QueueStatus } from '../models/queue.model';
import { SseResponse } from '../models/sse.model';
import { SseService } from './sse.service';

@Injectable({
  providedIn: 'root',
})
export class PhotoService extends AbstractApiService {
  private readonly _logger: Logger;

  private uploadImagesStatus = new Subject<QueueResponse[] | null>();
  private uploadImagesSubscription!: Subscription;

  constructor(
    private readonly httpClient: HttpClient,
    logService: LogService,
    private readonly sseService: SseService,
  ) {
    super(`${environment.backendServiceUrl}/v1/photo`, httpClient);
    this._logger = logService.createLogger(PhotoService.name);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createPath(path: string, query?: { [key: string]: any }) {
    return query ? [path, qs.stringify(query)].join('?') : path;
  }

  async uploadPaidPhotos(
    eventId: string,
    price: number,
    originalFiles: File[],
    watermarkFiles: File[],
  ) {
    const oriFileBatches = this.splitArrayToBatches<File>(
      originalFiles,
      environment.uploadImagePaidBatch,
    );
    const watFileBatches = this.splitArrayToBatches<File>(
      watermarkFiles,
      environment.uploadImagePaidBatch,
    );

    const uploadPromises = oriFileBatches.map(async (oriBatch, i) => {
      const photosMap = this.getPhotosMap(oriBatch, watFileBatches[i]);

      const formData = new FormData();
      formData.append('eventId', eventId);
      formData.append('price', String(price));
      oriBatch.forEach(o => formData.append('original', o));
      watFileBatches[i].forEach(w => formData.append('watermark', w));
      for (const [key, value] of Object.entries(photosMap)) {
        formData.append(`photosMap[${key}]`, value);
      }

      const res = await this.post<DataResponse<InitiateQueueResponse>>('/paid/upload', formData);
      return res.body?.data.queueId;
    });

    const queueIds = await Promise.all(uploadPromises);
    return queueIds.filter(id => id) as string[];

    // 💡 COMMENTED OUT: Sequential upload with throttle delay (kept for reference)
    // const queueIdsArr: string[] = [];
    // const lastIndex = oriFileBatches.length - 1;
    //
    // for (let i = 0; i <= lastIndex; i++) {
    //   const photosMap = this.getPhotosMap(oriFileBatches[i], watFileBatches[i]);
    //
    //   const formData = new FormData();
    //   formData.append('eventId', eventId);
    //   formData.append('price', String(price));
    //   oriFileBatches[i].forEach(o => formData.append('original', o));
    //   watFileBatches[i].forEach(w => formData.append('watermark', w));
    //   for (const [key, value] of Object.entries(photosMap)) {
    //     formData.append(`photosMap[${key}]`, value);
    //   }
    //
    //   const res = await this.post<DataResponse<InitiateQueueResponse>>('/paid/upload', formData);
    //   if (res.body) {
    //     queueIdsArr.push(res.body.data.queueId);
    //   }
    //
    //   // Throttle delay between batches (commented out for parallel uploads)
    //   if (i < lastIndex) {
    //     await this.delayAsync(environment.uploadImageThrottleDelay);
    //   }
    // }
    //
    // return queueIdsArr;
  }

  async uploadFreePhotos(eventId: string, files: File[]): Promise<string[]> {
    const fileBatches = this.splitArrayToBatches<File>(files, environment.uploadImageFreeBatch);

    const uploadPromises = fileBatches.map(async batch => {
      const formData = new FormData();
      formData.append('eventId', eventId);
      batch.forEach(b => formData.append('files', b));

      const res = await this.post<DataResponse<InitiateQueueResponse>>('/free/upload', formData);
      return res.body?.data.queueId;
    });

    const queueIds = await Promise.all(uploadPromises);
    return queueIds.filter(id => id) as string[];

    // 💡 COMMENTED OUT: Sequential upload with throttle delay (kept for reference)
    // const queueIdsArr: string[] = [];
    // const lastIndex = fileBatches.length - 1;
    //
    // for (const [index, batch] of fileBatches.entries()) {
    //   const formData = new FormData();
    //   formData.append('eventId', eventId);
    //   batch.forEach(b => formData.append('files', b));
    //
    //   const res = await this.post<DataResponse<InitiateQueueResponse>>('/free/upload', formData);
    //   if (res.body) {
    //     queueIdsArr.push(res.body.data.queueId);
    //   }
    //
    //   // Throttle delay between batches (commented out for parallel uploads)
    //   if (index < lastIndex) {
    //     await this.delayAsync(environment.uploadImageThrottleDelay);
    //   }
    // }
    //
    // return queueIdsArr;
  }

  async getPhotoById(photoId: string): Promise<PhotoEventResponse | null> {
    const res = await this.get<DataResponse<PhotoEventResponse>>(`/${photoId}`);
    return res.body?.data || null;
  }

  async downloadPhotoByIds(photoIds: string | string[]): Promise<void> {
    const res = await this.get<DataResponse<string[]>>(
      this.createPath('/download/photo', {
        ...(Array.isArray(photoIds) ? { 'photoIds[]': photoIds } : { photoIds }),
      }),
    );
    if (res.body) {
      const anchor = document.createElement('a');
      for (const p of res.body.data) {
        anchor.href = p;
        anchor.click();
        await this.delayAsync();
      }
      anchor.remove();
    }
  }

  getUploadImageStatuses(queueIds: string[]): Observable<QueueResponse[] | null> {
    this.uploadImagesSubscription = this.sseService
      .getServerSentEvent<SseResponse<QueueResponse[]>>(
        this.createPath(`${this.serviceBaseUrl}/upload-photo-status/stream`, {
          'queueIds[]': queueIds,
        }),
      )
      .subscribe(resp => {
        switch (resp.e) {
          case 'data':
            if (resp.data.every(q => q.status === QueueStatus.COMPLETED)) {
              this.uploadImagesStatus.next(resp.data);
              this.uploadImagesSubscription.unsubscribe();
              this.uploadImagesStatus.next(null);
            } else {
              this.uploadImagesStatus.next(resp.data);
            }
            break;
          case 'error':
            this.uploadImagesSubscription.unsubscribe();
            this.uploadImagesStatus.next(null);
            break;
          default:
            break;
        }
      });
    return this.uploadImagesStatus.asObservable();
  }

  async deletePhoto(photoId: string): Promise<void> {
    await this.delete<DataResponse<void>>(`/${photoId}`);
  }

  async getPaidPhotos(page: number) {
    const res = await this.get<DataResponse<PaginatedPaidPhotosResponse>>(
      this.createPath('/me/paid', {
        page,
      }),
    );
    return res.body?.data || null;
  }

  // ---

  private splitArrayToBatches<T>(arr: T[], n: number): T[][] {
    const res: T[][] = [];
    while (arr.length) {
      res.push(arr.splice(0, n));
    }
    return res;
  }

  private async delayAsync(t = 200): Promise<void> {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, t);
    });
  }

  private getPhotosMap(oriFilesArr: File[], watFilesArr: File[]): Record<string, string> {
    const photosMap: Record<string, string> = {};
    for (let i = 0; i < oriFilesArr.length; i++) {
      photosMap[oriFilesArr[i].name] = watFilesArr[i].name;
    }
    return photosMap;
  }
}
